import React, { useEffect, useRef } from "react";
import * as topojson from "topojson-client";
import * as d3 from "d3";
import { legend } from "../functions/utils";

export default function MapComponent({
  tipoMapa,
  dataFiltered,
  topologia,
  topologiaGS,
  baseTopo,
  dataProyectada,
  regionSelect,
}) {
  const svgRef = useRef(null);

  const filterComunasBy = (filter) => {
    const reducer = (key) => {
      return function reducer(accumulator, { properties }) {
        const { canonical_key } = properties;
        const dist = properties[key];
        if (accumulator[dist]) {
          accumulator[dist].push(canonical_key);
        } else {
          accumulator[dist] = [canonical_key];
        }
        return accumulator;
      };
    };
    let comunas = baseTopo.objects.base.geometries.reduce(reducer(filter), {});

    if (filter === "Region") {
      comunas["Gran Santiago"] = topologiaGS.objects.base.geometries.reduce(
        reducer(filter),
        {}
      ).undefined;
    }
    return comunas;
  };

  const getMapConf = (mapType) => {
    const geoObj = getGeoObj(mapType);
    const mapProjection = projection(geoObj);
    return {
      geoObj: geoObj,
      mapProjection: mapProjection,
    };
  };

  const getGeoObj = (mapType) => {
    const topo = topos[mapType];

    return {
      type: "GeometryCollection",
      geometries: topo.geometries.map((comuna) => {
        const mesh = topo.extra
          ? topojson.mesh(topologiaGS, comuna)
          : topojson.mesh(topologia, comuna);
        return {
          ...mesh,
          properties: {
            canonical_key: comuna.properties.canonical_key,
            centroid: d3.geoCentroid(mesh),
            comuna: comuna.properties.comuna,
          },
        };
      }),
    };
  };

  const filterTopologia = (filter, distList) => {
    const geometries = topologia.objects.base.geometries;
    if (filter === "Gran Santiago") {
      return {
        type: "GeometryCollection",
        geometries: topologiaGS.objects.base.geometries,
        extra: true,
      };
    }

    if (filter === "País") {
      return {
        type: "GeometryCollection",
        geometries: geometries.filter((geom) =>
          distList.includes(geom.properties.canonical_key)
        ),
      };
    }

    return {
      type: "GeometryCollection",
      geometries: geometries.filter((geom) =>
        distList[filter].includes(geom.properties.canonical_key)
      ),
    };
  };

  const dataBounds = (data) => {
    return [
      d3.format(".2r")(
        Math.min.apply(
          Math,
          data.map(function (o) {
            return o.value;
          })
        )
      ),
      d3.format(".2r")(
        Math.max.apply(
          Math,
          data.map(function (o) {
            return o.value;
          })
        )
      ),
    ];
  };

  const color = (
    data,
    maptype = undefined,
    val = false,
    tickThresholds = undefined
  ) => {
    let bounds =
      tickThresholds === undefined
        ? dataBounds(data)
        : [tickThresholds[0], tickThresholds[tickThresholds.length - 1]];
    let scale = d3.scaleQuantize(bounds, [
      "#D2DCFA",
      "#A6B9F5",
      "#7996F1",
      "#2150E8",
      "#1236ab",
    ]);
    if (val) {
      return scale(val);
    }
    return scale;
  };

  const map_color = (comuna, data, dataScale, tickThresholds, maptype) => {
    let val = data.find((el) => el.comuna_key === comuna);
    if (val.value === 0) {
      console.log("el valor es cero");
      return "#D2DCFA";
    }
    return Object.is(val.value, null)
      ? "#EDEDED"
      : color(dataScale, maptype, val.value, tickThresholds);
  };

  const projection = (geom) => {
    return d3.geoMercator().fitSize([600, 600], geom);
  };

  const comunasPorRegion = filterComunasBy("Region");
  const comunasPorPais = filterComunasBy("País");
  const comunasPorProvincia = filterComunasBy("Provincia");

  const provincias = Object.keys(comunasPorProvincia).sort();

  const topos = {
    Regional: filterTopologia(regionSelect, comunasPorRegion),
    Provincial: filterTopologia(provincias[0], comunasPorProvincia),
    País: filterTopologia("País", comunasPorPais.undefined),
  };

  const createTooltip = (d, data, maptype) => {
    const fallData = data.find(
      (el) => el.comuna_key === d.target.__data__.properties.canonical_key
    );
    if (fallData.value === null) {
      return `<strong>
              ${d.target.__data__.properties.comuna.toUpperCase()}
            </strong>
        <i class="texto-primario">
              S/I
            </i>
  `;
    }
    return `<strong>
              ${fallData.comuna}
            </strong>
  <span class="texto-primario">
            ${fallData.value.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
            </span>
  `;
  };

  useEffect(() => {
    const svgEl = d3.select(svgRef.current);
    svgEl.selectAll("*").remove(); // Clear svg content before adding new elements
    const svg = svgEl
      .attr("width", "100%")
      .attr("height", "80%")
      .style("width", "100%")
      .style("height", "100%")
      .attr("viewBox", function () {
        return tipoMapa === "País" ? "0 200 600 250" : "-300 0 1200 750";
      });

    let { geoObj, mapProjection } = getMapConf(tipoMapa);

    // Tooltip
    let div = d3.select(".arrow-box");

    //   Legend
    if (dataFiltered && dataFiltered.length) {
      svg
        .append("g")
        .attr("transform", function () {
          return tipoMapa === "País"
            ? `translate(200, 390)`
            : `translate(100, 650) `;
        })
        .append(() =>
          legend({
            color: color(dataProyectada),
            title: "Porcentaje de cotizantes",
            tickFormat: ".0f",
            width: tipoMapa === "País" ? 192 : 380,
            height: tipoMapa === "País" ? 42 : 64,
            titleFontSize: tipoMapa === "País" ? "7px" : "18px",
            titleFontWeight: "200",
            tickFontSize: tipoMapa === "País" ? "5px" : "15px",
            tickSize: tipoMapa === "País" ? 2 : 20,
            tickWidth: tipoMapa === "País" ? 0.8 : 1,
            tickPos: tipoMapa === "País" ? 1 : 7,
            tickThresholds: [0, 15, 30, 45, 60, 75],
            roundedValues: false,
            sin_info: true,
          })
        );
    }

    // Mapa
    svg
      .append("g")
      .selectAll("path")
      .data(geoObj.geometries)
      .enter()
      .append("path")
      .attr("class", function () {
        return tipoMapa === "País" ? "decideChilePais" : "decideChileRegion";
      })
      .attr("fill", (d, i) =>
        map_color(
          d.properties.canonical_key,
          dataFiltered,
          dataProyectada,
          [0, 15, 30, 45, 60, 75],
          "proyeccionNna"
        )
      )
      .attr("d", d3.geoPath(mapProjection))
      .attr("transform", function () {
        return tipoMapa === "País" ? "rotate(-90 300 300)" : null;
      })

      .on("mouseover", function (d, i) {
        svg.selectAll("path").attr("fill-opacity", 0.2);
        d3.select(this).transition().duration("25").attr("fill-opacity", 0.8);
        //Tooltip
        div
          .html(createTooltip(d, dataFiltered, "proyeccionNna"))
          .style("left", d.pageX + 20 + "px")
          .style("top", d.pageY + 20 + "px")
          .style(
            "box-shadow",
            "0 10px 16px 0 rgba(0,0,0,0.2),8px 6px 15px 0 rgba(0,0,0,0.19)"
          )
          .transition()
          .duration("25")
          .style("opacity", 1);
      })
      .on("mouseout", function (d) {
        svg.selectAll("path").attr("fill-opacity", 0.8);
        d3.select(this).transition().duration("25");
        //Tooltip
        div.transition().duration("25").style("opacity", 0);
      });

    svg
      .append("a")
      .attr("xlink:href", "https://unholster.com")
      .append("image")
      .attr(
        "xlink:href",
        "https://d2w3n1ggm96xe9.cloudfront.net/unholster/powered_by_unholster.svg"
      )
      .attr("height", function () {
        return tipoMapa === "País" ? 27 : 47;
      })
      .attr("transform", function () {
        return tipoMapa === "País"
          ? `translate(500, 400)`
          : `translate(600, 700) `;
      });
  });

  return <svg ref={svgRef} />;
}
